import {
  StandardViewType,
  ViewOrientationTargetKind,
  type StandardViewDefinition,
  type StandardViewCameraFrame,
  type ViewOrientationVector3Tuple,
} from './standardViewTypes';

/**
 * 默认世界朝上方向。
 */
const DEFAULT_WORLD_UP_VECTOR: ViewOrientationVector3Tuple = [0, 1, 0];

/**
 * 上视专用朝上方向。
 */
const TOP_VIEW_UP_VECTOR: ViewOrientationVector3Tuple = [0, 0, 1];

/**
 * 下视专用朝上方向。
 */
const BOTTOM_VIEW_UP_VECTOR: ViewOrientationVector3Tuple = [0, 0, -1];

/**
 * 创建标准视图定义。
 *
 * @param type 标准视图类型。
 * @param kind 命中目标类型。
 * @param label 展示名称。
 * @param direction 方向向量。
 * @param up 朝上向量。
 * @returns 标准视图定义。
 */
function createStandardViewDefinition(
  type: StandardViewType,
  kind: ViewOrientationTargetKind,
  label: string,
  direction: ViewOrientationVector3Tuple,
  up: ViewOrientationVector3Tuple = DEFAULT_WORLD_UP_VECTOR,
): StandardViewDefinition {
  return {
    type,
    kind,
    label,
    direction,
    up,
  };
}

/**
 * 标准视图定义表。
 *
 * 设计说明：
 * 1. 所有 26 向定义集中维护，避免 CameraManager 与宿主层重复声明；
 * 2. 这里的 direction 只描述方向，不绑定具体距离；
 * 3. Top/Bottom 视图显式指定 up，避免与观察方向平行。
 */
const STANDARD_VIEW_DEFINITION_MAP: Record<StandardViewType, StandardViewDefinition> = {
  [StandardViewType.Front]: createStandardViewDefinition(StandardViewType.Front, ViewOrientationTargetKind.Face, '前视', [0, 0, 1]),
  [StandardViewType.Back]: createStandardViewDefinition(StandardViewType.Back, ViewOrientationTargetKind.Face, '后视', [0, 0, -1]),
  [StandardViewType.Left]: createStandardViewDefinition(StandardViewType.Left, ViewOrientationTargetKind.Face, '左视', [-1, 0, 0]),
  [StandardViewType.Right]: createStandardViewDefinition(StandardViewType.Right, ViewOrientationTargetKind.Face, '右视', [1, 0, 0]),
  [StandardViewType.Top]: createStandardViewDefinition(StandardViewType.Top, ViewOrientationTargetKind.Face, '上视', [0, 1, 0], TOP_VIEW_UP_VECTOR),
  [StandardViewType.Bottom]: createStandardViewDefinition(StandardViewType.Bottom, ViewOrientationTargetKind.Face, '下视', [0, -1, 0], BOTTOM_VIEW_UP_VECTOR),
  [StandardViewType.FrontTop]: createStandardViewDefinition(StandardViewType.FrontTop, ViewOrientationTargetKind.Edge, '前上视', [0, 1, 1]),
  [StandardViewType.FrontBottom]: createStandardViewDefinition(StandardViewType.FrontBottom, ViewOrientationTargetKind.Edge, '前下视', [0, -1, 1]),
  [StandardViewType.FrontLeft]: createStandardViewDefinition(StandardViewType.FrontLeft, ViewOrientationTargetKind.Edge, '前左视', [-1, 0, 1]),
  [StandardViewType.FrontRight]: createStandardViewDefinition(StandardViewType.FrontRight, ViewOrientationTargetKind.Edge, '前右视', [1, 0, 1]),
  [StandardViewType.BackTop]: createStandardViewDefinition(StandardViewType.BackTop, ViewOrientationTargetKind.Edge, '后上视', [0, 1, -1]),
  [StandardViewType.BackBottom]: createStandardViewDefinition(StandardViewType.BackBottom, ViewOrientationTargetKind.Edge, '后下视', [0, -1, -1]),
  [StandardViewType.BackLeft]: createStandardViewDefinition(StandardViewType.BackLeft, ViewOrientationTargetKind.Edge, '后左视', [-1, 0, -1]),
  [StandardViewType.BackRight]: createStandardViewDefinition(StandardViewType.BackRight, ViewOrientationTargetKind.Edge, '后右视', [1, 0, -1]),
  [StandardViewType.TopLeft]: createStandardViewDefinition(StandardViewType.TopLeft, ViewOrientationTargetKind.Edge, '上左视', [-1, 1, 0]),
  [StandardViewType.TopRight]: createStandardViewDefinition(StandardViewType.TopRight, ViewOrientationTargetKind.Edge, '上右视', [1, 1, 0]),
  [StandardViewType.BottomLeft]: createStandardViewDefinition(StandardViewType.BottomLeft, ViewOrientationTargetKind.Edge, '下左视', [-1, -1, 0]),
  [StandardViewType.BottomRight]: createStandardViewDefinition(StandardViewType.BottomRight, ViewOrientationTargetKind.Edge, '下右视', [1, -1, 0]),
  [StandardViewType.FrontTopLeft]: createStandardViewDefinition(StandardViewType.FrontTopLeft, ViewOrientationTargetKind.Corner, '前上左视', [-1, 1, 1]),
  [StandardViewType.FrontTopRight]: createStandardViewDefinition(StandardViewType.FrontTopRight, ViewOrientationTargetKind.Corner, '前上右视', [1, 1, 1]),
  [StandardViewType.FrontBottomLeft]: createStandardViewDefinition(StandardViewType.FrontBottomLeft, ViewOrientationTargetKind.Corner, '前下左视', [-1, -1, 1]),
  [StandardViewType.FrontBottomRight]: createStandardViewDefinition(StandardViewType.FrontBottomRight, ViewOrientationTargetKind.Corner, '前下右视', [1, -1, 1]),
  [StandardViewType.BackTopLeft]: createStandardViewDefinition(StandardViewType.BackTopLeft, ViewOrientationTargetKind.Corner, '后上左视', [-1, 1, -1]),
  [StandardViewType.BackTopRight]: createStandardViewDefinition(StandardViewType.BackTopRight, ViewOrientationTargetKind.Corner, '后上右视', [1, 1, -1]),
  [StandardViewType.BackBottomLeft]: createStandardViewDefinition(StandardViewType.BackBottomLeft, ViewOrientationTargetKind.Corner, '后下左视', [-1, -1, -1]),
  [StandardViewType.BackBottomRight]: createStandardViewDefinition(StandardViewType.BackBottomRight, ViewOrientationTargetKind.Corner, '后下右视', [1, -1, -1]),
};

/**
 * 将输入向量归一化为单位向量。
 *
 * @param vector 输入向量。
 * @returns 归一化后的向量。
 */
function normalizeVector(vector: ViewOrientationVector3Tuple): ViewOrientationVector3Tuple {
  const length = Math.hypot(vector[0], vector[1], vector[2]);

  // 视图方向属于静态协议。如果长度异常，直接返回原值，便于上层快速暴露错误配置。
  if (length <= Number.EPSILON) {
    return vector;
  }

  return [vector[0] / length, vector[1] / length, vector[2] / length];
}

/**
 * 读取单个标准视图定义。
 *
 * @param viewType 标准视图类型。
 * @returns 标准视图定义。
 */
export function getStandardViewDefinition(viewType: StandardViewType): StandardViewDefinition {
  return STANDARD_VIEW_DEFINITION_MAP[viewType];
}

/**
 * 读取全部标准视图定义。
 *
 * @returns 标准视图定义数组。
 */
export function listStandardViewDefinitions(): StandardViewDefinition[] {
  return Object.values(STANDARD_VIEW_DEFINITION_MAP);
}

/**
 * 构建标准视图相机帧。
 *
 * @param input 计算输入。
 * @param input.viewType 标准视图类型。
 * @param input.target 当前观察目标。
 * @param input.distance 当前相机距离。
 * @returns 相机帧。
 */
export function buildStandardViewCameraFrame(input: {
  viewType: StandardViewType;
  target: ViewOrientationVector3Tuple;
  distance: number;
}): StandardViewCameraFrame {
  const definition = getStandardViewDefinition(input.viewType);
  const normalizedDirection = normalizeVector(definition.direction);
  const distance = input.distance > Number.EPSILON ? input.distance : 1;

  // 使用当前 target 与距离重建相机位置，确保标准视图切换不会意外改变缩放层级。
  const position: ViewOrientationVector3Tuple = [
    input.target[0] + normalizedDirection[0] * distance,
    input.target[1] + normalizedDirection[1] * distance,
    input.target[2] + normalizedDirection[2] * distance,
  ];

  return {
    position,
    target: input.target,
    up: definition.up,
  };
}